// Line-count audit (2026-05-04): 774 lines.
import { useCallback } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { propertyModificationService } from '@/services/ai/PropertyModificationService';
import { componentReferenceResolver } from '@/services/ai/ComponentReferenceResolver';
import { IntentClassificationService } from '@/services/ai/IntentClassificationService';
import { IntentCategory } from '@/services/ai/types';
import { clarificationService } from '@/services/ai/ClarificationService';
import { buildAgentContext } from '@/utils/agentContextBuilder';
import {
  resolveScopedChildGroup,
  resolveScopedParentContainer,
} from '@/utils/preBackendReferenceUtils';
import { isLikelyAgentQuery } from './useCanvasTextParser';
import { Component, ComponentType } from '@/types';
import type { PropertyMessage } from '@/components/ai/types';
import type { ClarificationRequest } from '@/types/ai';
import type { PropertyChange } from '@/services/ai/commands/BaseCommandHandler';
import type {
  PreBackendMessageResult,
  UsePreBackendIntentRoutingDeps,
  UsePreBackendIntentRoutingReturn,
} from './agentMessagingTypes';
import {
  getBlockedAICommandPermissionMessage,
  getMutationCategoriesForChanges,
} from '@/services/ai/agentCommandPermissions';

const COMPONENT_GROUP_PLANNING_BATCH_SIZE = 8;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

async function mapInBatches<T, R>(
  items: T[],
  batchSize: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = [];

  for (let startIndex = 0; startIndex < items.length; startIndex += batchSize) {
    const batch = items.slice(startIndex, startIndex + batchSize);
    const batchResults = await Promise.all(
      batch.map((item, batchIndex) => mapper(item, startIndex + batchIndex))
    );
    results.push(...batchResults);
  }

  return results;
}

function stripResolvedTargetReference(command: string, references: string[]): string {
  let stripped = command;

  for (const reference of references) {
    const normalizedReference = reference.trim();
    if (!normalizedReference) {
      continue;
    }

    stripped = stripped.replace(
      new RegExp(`\\b(?:the\\s+|this\\s+|that\\s+)?${escapeRegExp(normalizedReference)}\\b`, 'ig'),
      ''
    );
  }

  return stripped
    .replace(/\s+/g, ' ')
    .replace(/\s+([?.!,])/g, '$1')
    .replace(/[?.!]+$/g, '')
    .trim();
}

function toPropertyChangeRecord(
  changes: PropertyChange | undefined
): Record<string, unknown> | undefined {
  if (!changes) {
    return undefined;
  }

  return Object.fromEntries(Object.entries(changes));
}

export function usePreBackendIntentRouting(
  deps: UsePreBackendIntentRoutingDeps
): UsePreBackendIntentRoutingReturn {
  const {
    component,
    multipleComponents,
    isCanvas,
    agentStatus,
    project,
    storePages,
    setMessages,
    setInputValue,
    setIsLoading,
    setCurrentGenerationMessageId,
    setPendingClarification,
    persistMessage,
    generateMessageId,
    generatePageFromDescription,
    isSlashCommand,
    executeSlashCommand,
    applyPropertyChanges,
    routeToAgent,
    commandPermissionGuard,
  } = deps;

  const handleIntentRoutedPreBackendMessage = useCallback(
    async (userInput: string): Promise<PreBackendMessageResult> => {
      const lowerInput = userInput.toLowerCase();
      const appendUserMessage = async (): Promise<void> => {
        const userMessage: PropertyMessage = {
          id: generateMessageId(),
          content: userInput,
          role: 'user',
          timestamp: new Date(),
        };
        setMessages((previous) => [...previous, userMessage]);
        await persistMessage(userMessage);
        setInputValue('');
      };

      const appendBlockedAssistantMessage = async (content: string): Promise<void> => {
        const assistantMessage: PropertyMessage = {
          id: generateMessageId(),
          content,
          role: 'assistant',
          timestamp: new Date(),
          applied: false,
        };
        setMessages((previous) => [...previous, assistantMessage]);
        await persistMessage(assistantMessage);
        setIsLoading(false);
      };

      const targetForAmbiguityCheck = component || multipleComponents?.[0] || null;
      const allComponents = useEditorStore.getState().components;
      const selectedComponentIds = useEditorStore.getState().selectedComponents;
      const currentSelectedComponents = selectedComponentIds
        .map((id: string) => allComponents.find((componentItem) => componentItem.id === id))
        .filter((componentItem): componentItem is Component => componentItem !== undefined);

      const hasMultipleComponentsSelected = Boolean(multipleComponents && multipleComponents.length >= 2);
      const looksLikeFormSchemaRequest = /form/i.test(userInput) && /schema|database|submit/i.test(userInput);
      const isCreationCommand = /^(create|add|make|insert|new)\s+(a\s+)?/i.test(lowerInput);
      // Multi-target positional commands reference multiple positional groups — skip frontend
      // disambiguation and let the agent handle it. Two forms:
      // 1. Adjacent pairs: "top row blue, second row red" (≥2 ordinal+noun pairs)
      // 2. Shared noun: "top and bottom rows" (≥2 ordinals + any positional noun)
      const positionalOrdinals = (userInput.match(/\b(top|first|second|third|fourth|fifth|middle|bottom|last)\b/gi) || []).length;
      const hasPositionalNoun = /\b(rows?|columns?|cards?|sections?|groups?)\b/i.test(userInput);
      const isMultiTargetPositionalCommand = positionalOrdinals >= 2 && hasPositionalNoun;
      const shouldResolveReferences =
        !component &&
        !hasMultipleComponentsSelected &&
        !isSlashCommand(userInput) &&
        !looksLikeFormSchemaRequest &&
        !isCreationCommand &&
        !isMultiTargetPositionalCommand &&
        !isLikelyAgentQuery(userInput);

      let resolvedComponent: Component | null = null;
      let resolvedComponentGroup: Component[] = [];
      let resolvedGroupCommand: string | null = null;
      let resolvedGroupLabel: string | null = null;
      let resolvedParentContainer: Component | null = null;
      let resolvedParentCommand: string | null = null;
      let resolvedParentLabel: string | null = null;
      let resolvedComponentCommand: string | null = null;

      if (shouldResolveReferences) {
        const scopedGroupResolution = resolveScopedChildGroup(
          userInput,
          allComponents,
          currentSelectedComponents,
          project?.currentPageId
        );
        if (scopedGroupResolution) {
          resolvedComponentGroup = scopedGroupResolution.targets;
          resolvedGroupCommand = scopedGroupResolution.actionCommand;
          resolvedGroupLabel = scopedGroupResolution.targetLabel;
        }

        const scopedParentResolution = resolveScopedParentContainer(userInput, allComponents);
        if (scopedParentResolution) {
          resolvedParentContainer = scopedParentResolution.target;
          resolvedParentCommand = scopedParentResolution.actionCommand;
          resolvedParentLabel = scopedParentResolution.targetLabel;
        }

        if (resolvedComponentGroup.length === 0 && !resolvedParentContainer) {
          const references = componentReferenceResolver.extractComponentReferences(userInput);

          if (references.length > 0) {
            let resolutionResult = null;
            let successfulReference = '';

            for (const reference of references) {
              const result = componentReferenceResolver.resolveSemanticReference(reference, {
                selectedComponents: currentSelectedComponents,
                pageId: project?.currentPageId,
              });

              if (result.success || result.needsClarification) {
                resolutionResult = result;
                successfulReference = reference;
                break;
              }
            }

            if (!resolutionResult) {
              resolutionResult = componentReferenceResolver.resolveSemanticReference(references[0], {
                selectedComponents: currentSelectedComponents,
                pageId: project?.currentPageId,
              });
              successfulReference = references[0];
            }

            if (resolutionResult.success && resolutionResult.primaryMatch) {
              resolvedComponent = allComponents.find(
                (componentItem) => componentItem.id === resolutionResult.primaryMatch?.resolvedComponentId
              ) || null;
              resolvedComponentCommand = stripResolvedTargetReference(userInput, [
                successfulReference,
                ...references,
              ]);
            } else if (resolutionResult.needsClarification && resolutionResult.clarificationOptions) {
              const clarificationRequest: ClarificationRequest = {
                id: `sem-${Date.now()}`,
                type: 'component_selection',
                question: `I found ${resolutionResult.allMatches.length} components that could match "${successfulReference}". Which one did you mean?`,
                options: resolutionResult.clarificationOptions,
                allowCustomInput: false,
                context: {
                  originalCommand: userInput,
                  detectedIntent: 'component_reference',
                  matchedComponents: resolutionResult.allMatches.map((match) => ({
                    id: match.id,
                    name: match.name,
                    type: String(match.type),
                    confidence: match.confidence,
                    matchReason: match.matchReason,
                    semanticRole: match.semanticRole,
                  })),
                },
                createdAt: new Date(),
              };

              await appendUserMessage();
              clarificationService.registerClarification(clarificationRequest);
              setPendingClarification(clarificationRequest);

              const matchList = resolutionResult.allMatches
                .slice(0, 4)
                .map((match, index) => `${index + 1}. "${match.name}" (${match.type}) - ${match.matchReason}`)
                .join('\n');
              const clarificationMessage: PropertyMessage = {
                id: generateMessageId(),
                content: `${clarificationRequest.question}\n\n${matchList}`,
                role: 'assistant',
                timestamp: new Date(),
              };
              setMessages((previous) => [...previous, clarificationMessage]);
              await persistMessage(clarificationMessage);

              return { handled: true, effectiveComponent: component };
            }
          }
        }
      }

      const shouldCheckAmbiguity =
        !component &&
        !resolvedComponent &&
        resolvedComponentGroup.length === 0 &&
        !resolvedParentContainer &&
        !isSlashCommand(userInput) &&
        !looksLikeFormSchemaRequest &&
        !isCreationCommand &&
        !isMultiTargetPositionalCommand &&
        !isLikelyAgentQuery(userInput);

      if (shouldCheckAmbiguity) {
        const ambiguityResult = clarificationService.detectAmbiguity(
          userInput,
          targetForAmbiguityCheck,
          allComponents
        );

        if (ambiguityResult.isAmbiguous) {
          const clarificationRequest = clarificationService.generateClarificationRequest(
            ambiguityResult,
            userInput,
            targetForAmbiguityCheck
          );

          if (clarificationRequest) {
            await appendUserMessage();
            setPendingClarification(clarificationRequest);

            const clarificationMessage: PropertyMessage = {
              id: generateMessageId(),
              content: clarificationRequest.question,
              role: 'assistant',
              timestamp: new Date(),
            };
            setMessages((previous) => [...previous, clarificationMessage]);
            await persistMessage(clarificationMessage);

            return { handled: true, effectiveComponent: component };
          }
        }
      }

      const effectiveComponent = resolvedComponent || component;

      if (!component && resolvedComponent && resolvedComponentCommand && !isCanvas) {
        const freshTarget = useEditorStore.getState().components.find(
          (item) => item.id === resolvedComponent.id
        ) || resolvedComponent;
        const changes = await propertyModificationService.parsePropertyChangesAsync(
          resolvedComponentCommand,
          freshTarget
        );

        if (changes && Object.keys(changes).length > 0) {
          const permissionResult = commandPermissionGuard?.({
            categories: getMutationCategoriesForChanges(toPropertyChangeRecord(changes)),
            project,
            targetComponents: [freshTarget],
          });

          await appendUserMessage();

          if (permissionResult && !permissionResult.allowed) {
            await appendBlockedAssistantMessage(
              getBlockedAICommandPermissionMessage(permissionResult, 'content')
            );
            return { handled: true, effectiveComponent: freshTarget };
          }

          setIsLoading(true);

          const assistantMessageId = generateMessageId();
          const finalContent = `✓ Applied "${resolvedComponentCommand}" to ${freshTarget.name || freshTarget.type}.`;
          const assistantMessage: PropertyMessage = {
            id: assistantMessageId,
            content: finalContent,
            role: 'assistant',
            timestamp: new Date(),
            applied: true,
          };
          setMessages((previous) => [...previous, assistantMessage]);

          applyPropertyChanges(assistantMessageId, {
            ...changes,
            targetComponentId: freshTarget.id,
          });

          await persistMessage(assistantMessage);

          setIsLoading(false);
          return { handled: true, effectiveComponent: freshTarget };
        }
      }

      if (!component && resolvedComponentGroup.length >= 2 && resolvedGroupCommand) {
        const initialTarget = useEditorStore.getState().components.find(
          (item) => item.id === resolvedComponentGroup[0].id
        ) || resolvedComponentGroup[0];
        const initialChanges = await propertyModificationService.parsePropertyChangesAsync(resolvedGroupCommand, initialTarget);

        if (initialChanges) {
          const plannedChanges = (await mapInBatches(
            resolvedComponentGroup,
            COMPONENT_GROUP_PLANNING_BATCH_SIZE,
            async (target) => {
              const freshTarget = useEditorStore.getState().components.find((item) => item.id === target.id) || target;
              const changes = await propertyModificationService.parsePropertyChangesAsync(resolvedGroupCommand, freshTarget);
              return changes ? { target: freshTarget, changes } : null;
            }
          )).filter((planned): planned is { target: Component; changes: PropertyChange } => planned !== null);

          const permissionResult = commandPermissionGuard?.({
            categories: plannedChanges.flatMap((planned) =>
              getMutationCategoriesForChanges(toPropertyChangeRecord(planned.changes))
            ),
            project,
            targetComponents: plannedChanges.map((planned) => planned.target),
          });

          await appendUserMessage();

          if (permissionResult && !permissionResult.allowed) {
            await appendBlockedAssistantMessage(
              getBlockedAICommandPermissionMessage(permissionResult, 'content')
            );
            return { handled: true, effectiveComponent };
          }

          setIsLoading(true);

          const assistantMessageId = generateMessageId();
          setMessages((previous) => [...previous, {
            id: assistantMessageId,
            content: `Applying changes to ${resolvedComponentGroup.length} ${resolvedGroupLabel || 'components'}...`,
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: true,
          }]);

          let appliedCount = 0;
          for (const { target: freshTarget, changes } of plannedChanges) {
            applyPropertyChanges(assistantMessageId, {
              ...changes,
              targetComponentId: freshTarget.id,
            });
            appliedCount += 1;
          }

          const finalContent = appliedCount > 0
            ? `✓ Applied "${resolvedGroupCommand}" to ${appliedCount} ${resolvedGroupLabel || 'components'}.`
            : `I understood the target group "${resolvedGroupLabel || 'components'}", but I couldn't apply "${resolvedGroupCommand}".`;
          setMessages((previous) => previous.map((message) =>
            message.id === assistantMessageId
              ? { ...message, content: finalContent, isStreaming: false, applied: appliedCount > 0 }
              : message
          ));
          await persistMessage({
            id: assistantMessageId,
            content: finalContent,
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: false,
          });

          setIsLoading(false);
          return { handled: true, effectiveComponent };
        }
      }

      if (!component && resolvedParentContainer && resolvedParentCommand) {
        const freshTarget = useEditorStore.getState().components.find(
          (item) => item.id === resolvedParentContainer.id
        ) || resolvedParentContainer;
        const changes = await propertyModificationService.parsePropertyChangesAsync(resolvedParentCommand, freshTarget);

        if (changes) {
          const permissionResult = commandPermissionGuard?.({
            categories: getMutationCategoriesForChanges(toPropertyChangeRecord(changes)),
            project,
            targetComponents: [freshTarget],
          });

          await appendUserMessage();

          if (permissionResult && !permissionResult.allowed) {
            await appendBlockedAssistantMessage(
              getBlockedAICommandPermissionMessage(permissionResult, 'content')
            );
            return { handled: true, effectiveComponent };
          }

          setIsLoading(true);

          const assistantMessageId = generateMessageId();
          setMessages((previous) => [...previous, {
            id: assistantMessageId,
            content: `Applying changes to ${resolvedParentLabel || 'container'}...`,
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: true,
          }]);

          applyPropertyChanges(assistantMessageId, {
            ...changes,
            targetComponentId: freshTarget.id,
          });

          const finalContent = `✓ Applied "${resolvedParentCommand}" to ${resolvedParentLabel || 'container'}.`;
          setMessages((previous) => previous.map((message) =>
            message.id === assistantMessageId
              ? { ...message, content: finalContent, isStreaming: false, applied: true }
              : message
          ));
          await persistMessage({
            id: assistantMessageId,
            content: finalContent,
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: false,
          });

          setIsLoading(false);
          return { handled: true, effectiveComponent };
        }
      }

      if (effectiveComponent && !isCanvas) {
        if (isLikelyAgentQuery(userInput) && agentStatus.available) {
          await appendUserMessage();
          setIsLoading(true);

          const loadingId = generateMessageId();
          const loadingMessage: PropertyMessage = {
            id: loadingId,
            content: 'Consulting agents...',
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: true,
            metadata: { source: 'agent' },
          };
          setMessages((previous) => [...previous, loadingMessage]);

          try {
            const editorStoreState = useEditorStore.getState();
            await routeToAgent(
              userInput,
              buildAgentContext(storePages, isCanvas ?? false, component, project, {
                currentPageId: editorStoreState.currentPageId,
                currentPageComponents: editorStoreState.components,
                pageComponents: editorStoreState.pageComponents,
              }),
              loadingId
            );
          } catch (agentError) {
            console.warn('⚠️ Agent query with component selected failed:', agentError);
            setMessages((previous) => previous.filter((message) => message.id !== loadingId));

            const fallbackMessage: PropertyMessage = {
              id: generateMessageId(),
              content: 'I couldn\'t reach the agent service to answer that question. Please try again in a moment.',
              role: 'assistant',
              timestamp: new Date(),
            };
            setMessages((previous) => [...previous, fallbackMessage]);
            await persistMessage(fallbackMessage);
          }

          setIsLoading(false);
          return { handled: true, effectiveComponent };
        }

        setIsLoading(true);
        return { handled: false, effectiveComponent };
      }

      setIsLoading(true);

      try {
        const classification = await IntentClassificationService.getInstance().classifyIntent(userInput, {
          hasSelectedComponent: Boolean(effectiveComponent),
          isCanvasEmpty: !useEditorStore.getState().components.length,
          componentType: effectiveComponent?.type,
        });

        if (classification.intent === IntentCategory.PAGE_GENERATION && isCanvas) {
          const permissionResult = commandPermissionGuard?.({
            categories: ['canvas', 'insertion'],
            project,
            isCanvasMutation: true,
          });

          await appendUserMessage();

          if (permissionResult && !permissionResult.allowed) {
            await appendBlockedAssistantMessage(
              getBlockedAICommandPermissionMessage(permissionResult, 'canvas')
            );
            return { handled: true, effectiveComponent };
          }

          setIsLoading(true);

          const assistantMessageId = generateMessageId();
          setMessages((previous) => [...previous, {
            id: assistantMessageId,
            content: 'Starting page generation...',
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: true,
          }]);

          setCurrentGenerationMessageId(assistantMessageId);

          try {
            await generatePageFromDescription(userInput);

            const state = useEditorStore.getState();
            const stage = state.aiGenerationStage;
            const error = state.aiGenerationError;
            const metadata = state.aiGenerationMetadata;

            if (
              stage === 'complete' &&
              metadata &&
              typeof metadata === 'object' &&
              'selectedTemplates' in metadata &&
              Array.isArray(metadata.selectedTemplates) &&
              'latency' in metadata &&
              typeof metadata.latency === 'object' &&
              metadata.latency !== null &&
              'total' in metadata.latency &&
              typeof metadata.latency.total === 'number' &&
              'confidence' in metadata &&
              typeof metadata.confidence === 'number'
            ) {
              const successMessage = `✅ Page generated successfully!\n\nUsed ${metadata.selectedTemplates.length} template(s) in ${metadata.latency.total}ms\nConfidence: ${Math.round(metadata.confidence * 100)}%\n\nComponents have been added to the canvas!`;
              setMessages((previous) => previous.map((message) =>
                message.id === assistantMessageId
                  ? { ...message, content: successMessage, isStreaming: false }
                  : message
              ));
              await persistMessage({
                id: assistantMessageId,
                content: successMessage,
                role: 'assistant',
                timestamp: new Date(),
                isStreaming: false,
              });
            } else if (stage === 'error' || error) {
              const errorMessage = `❌ Generation failed\n\n${error || 'An unknown error occurred'}`;
              setMessages((previous) => previous.map((message) =>
                message.id === assistantMessageId
                  ? { ...message, content: errorMessage, isStreaming: false }
                  : message
              ));
              await persistMessage({
                id: assistantMessageId,
                content: errorMessage,
                role: 'assistant',
                timestamp: new Date(),
                isStreaming: false,
              });
            } else {
              const unexpectedMessage = '⚠️ Generation completed with unexpected state';
              setMessages((previous) => previous.map((message) =>
                message.id === assistantMessageId
                  ? { ...message, content: unexpectedMessage, isStreaming: false }
                  : message
              ));
              await persistMessage({
                id: assistantMessageId,
                content: unexpectedMessage,
                role: 'assistant',
                timestamp: new Date(),
                isStreaming: false,
              });
            }

            setCurrentGenerationMessageId(null);
            setIsLoading(false);
            return { handled: true, effectiveComponent };
          } catch (error: unknown) {
            const errorMessage = `❌ Error: ${error instanceof Error ? error.message : 'An unexpected error occurred during page generation'}`;
            setMessages((previous) => previous.map((message) =>
              message.id === assistantMessageId
                ? { ...message, content: errorMessage, isStreaming: false }
                : message
            ));
            await persistMessage({
              id: assistantMessageId,
              content: errorMessage,
              role: 'assistant',
              timestamp: new Date(),
              isStreaming: false,
            });
            setCurrentGenerationMessageId(null);
            setIsLoading(false);
            return { handled: true, effectiveComponent };
          }
        }

        if (classification.intent === IntentCategory.COMPONENT_MODIFICATION) {
          const creationMatch = lowerInput.match(/^(?:create|add|make|insert|new)\s+(?:a\s+)?(.+)$/i);
          const isMultiComponent = /,\s*(?:a\s+|an\s+)?(?:new\s+)?[\w-]+|(?:\s+and\s+)(?:a\s+|an\s+)?(?:new\s+)?[\w-]+\s*(?:chart|text|button|image|input|container|table|gauge|list)/i.test(lowerInput);

          if (creationMatch && isCanvas && !isMultiComponent) {
            const permissionResult = commandPermissionGuard?.({
              categories: ['insertion'],
              project,
              isCanvasMutation: true,
            });

            await appendUserMessage();

            if (permissionResult && !permissionResult.allowed) {
              await appendBlockedAssistantMessage(
                getBlockedAICommandPermissionMessage(permissionResult, 'insertion')
              );
              return { handled: true, effectiveComponent };
            }

            try {
              const result = await executeSlashCommand(`/add ${creationMatch[1].trim()}`, null, isCanvas);
              const assistantMessage: PropertyMessage = {
                id: generateMessageId(),
                content: result.message,
                role: 'assistant',
                timestamp: new Date(),
                propertyChanges: result.propertyChanges,
              };
              setMessages((previous) => [...previous, assistantMessage]);
              await persistMessage(assistantMessage);
              result.applyMutations?.();
            } catch (commandError: unknown) {
              const errorMessage: PropertyMessage = {
                id: generateMessageId(),
                content: `❌ Failed to create component: ${commandError instanceof Error ? commandError.message : 'Unknown error'}`,
                role: 'assistant',
                timestamp: new Date(),
              };
              setMessages((previous) => [...previous, errorMessage]);
              await persistMessage(errorMessage);
            }

            setIsLoading(false);
            return { handled: true, effectiveComponent };
          }
        } else if (classification.intent === IntentCategory.UNCLEAR) {
          if (agentStatus.available) {
            const loadingId = generateMessageId();
            const loadingMessage: PropertyMessage = {
              id: loadingId,
              content: 'Consulting agents...',
              role: 'assistant',
              timestamp: new Date(),
              isStreaming: true,
              metadata: { source: 'agent' },
            };
            setMessages((previous) => [...previous, loadingMessage]);

            try {
              const editorStoreState = useEditorStore.getState();
              await routeToAgent(
                userInput,
                buildAgentContext(storePages, isCanvas ?? false, component, project, {
                  currentPageId: editorStoreState.currentPageId,
                  currentPageComponents: editorStoreState.components,
                  pageComponents: editorStoreState.pageComponents,
                }),
                loadingId
              );
            } catch (agentError) {
              console.warn('⚠️ Agent service unavailable, using fallback:', agentError);
              setMessages((previous) => previous.filter((message) => message.id !== loadingId));

              const fallbackMessage: PropertyMessage = {
                id: generateMessageId(),
                content: `I'm not sure what you'd like me to do. Could you rephrase your request?\n\nHere are some things I can help with:\n- **Generate a page**: "generate a dashboard for soil moisture sensors"\n- **Add components**: "add a chart here"\n- **Modify properties**: "make the title bigger"`,
                role: 'assistant',
                timestamp: new Date(),
                metadata: { source: 'agent' },
              };
              setMessages((previous) => [...previous, fallbackMessage]);
              await persistMessage(fallbackMessage);
            }
          } else {
            const fallbackMessage: PropertyMessage = {
              id: generateMessageId(),
              content: `I'm not sure what you'd like me to do. Could you rephrase your request?\n\nHere are some things I can help with:\n- **Generate a page**: "generate a dashboard for soil moisture sensors"\n- **Add components**: "add a chart here"\n- **Modify properties**: "make the title bigger"`,
              role: 'assistant',
              timestamp: new Date(),
              metadata: { source: 'agent' },
            };
            setMessages((previous) => [...previous, fallbackMessage]);
            await persistMessage(fallbackMessage);
          }

          setIsLoading(false);
          return { handled: true, effectiveComponent };
        }
      } catch (classificationError: unknown) {
        console.error('❌ Intent classification failed:', classificationError);
      }

      return { handled: false, effectiveComponent };
    },
    [
      component,
      multipleComponents,
      isCanvas,
      agentStatus,
      project,
      storePages,
      setMessages,
      setInputValue,
      setIsLoading,
      setCurrentGenerationMessageId,
      setPendingClarification,
      persistMessage,
      generateMessageId,
      generatePageFromDescription,
      isSlashCommand,
      executeSlashCommand,
      applyPropertyChanges,
      routeToAgent,
      commandPermissionGuard,
    ]
  );

  return { handleIntentRoutedPreBackendMessage };
}
